import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';

export type RefreshStateArea = 'home' | 'dashboard' | 'analytics' | 'reports' | 'settings';

interface StoredRefreshState<T> {
  savedAt: string;
  state: T;
}

const REFRESH_STATE_PREFIX = 'smartforms.refresh-state.v1';
const ROUTER_INTERNAL_STATE_KEYS = new Set(['navigationId', 'ɵrouterPageId']);

function currentUserScope(): string {
  const user = localStorage.getItem('email')?.trim().toLowerCase() || 'anonymous';
  return encodeURIComponent(user);
}

function storageKey(key: string): string {
  return `${REFRESH_STATE_PREFIX}:${currentUserScope()}:${key}`;
}

function currentUserStoragePrefix(): string {
  return `${REFRESH_STATE_PREFIX}:${currentUserScope()}:`;
}

/** Removes only the signed-in user's reload state, leaving other users' tab state isolated. */
export function clearRefreshStateForCurrentUser(): void {
  try {
    const prefix = currentUserStoragePrefix();
    const matchingKeys: string[] = [];

    for (let index = 0; index < sessionStorage.length; index += 1) {
      const key = sessionStorage.key(index);
      if (key?.startsWith(prefix)) matchingKeys.push(key);
    }

    matchingKeys.forEach(key => sessionStorage.removeItem(key));
  } catch {
    // Storage cleanup is best-effort and must not prevent logout.
  }
}

function normalizeRouteUrl(url: string): string {
  const normalized = (url || '/').replace(/^#/, '');
  return normalized.startsWith('/') ? normalized : `/${normalized}`;
}

function customNavigationState(state: unknown): Record<string, unknown> | null {
  if (!state || typeof state !== 'object') return null;

  const entries = Object.entries(state as Record<string, unknown>)
    .filter(([key]) => !ROUTER_INTERNAL_STATE_KEYS.has(key));
  return entries.length > 0 ? Object.fromEntries(entries) : null;
}

/**
 * Keycloak is initialized before Angular. Its redirect round-trip can recreate the browser
 * history entry without Angular's custom navigation state, so restore that payload before
 * the router instantiates any routed components.
 */
export function restoreNavigationStateBeforeBootstrap(): void {
  try {
    if (customNavigationState(history.state)) return;

    const routeUrl = normalizeRouteUrl(window.location.hash || '/');
    const raw = sessionStorage.getItem(storageKey(`navigation:${routeUrl}`));
    if (!raw) return;

    const entry = JSON.parse(raw) as StoredRefreshState<Record<string, unknown>>;
    if (!entry?.state || typeof entry.state !== 'object') return;
    history.replaceState(entry.state, document.title, window.location.href);
  } catch {
    // Navigation restoration is best-effort and must never block application startup.
  }
}

/**
 * Keeps non-authoritative UI and in-progress entry state across a page refresh.
 *
 * Session storage is intentionally used instead of local storage: the state survives
 * reloads in the current tab, but is removed when the tab is closed. Each key is also
 * scoped to the signed-in user so one user cannot restore another user's screen state.
 */
@Injectable({
  providedIn: 'root'
})
export class RefreshStateService {
  private readonly areaResetSubject = new Subject<RefreshStateArea>();
  private readonly resettingAreas = new Set<RefreshStateArea>();
  private readonly resetFallbackTimers = new Map<RefreshStateArea, ReturnType<typeof setTimeout>>();

  /** Emitted only for an intentional re-click of the currently active main menu. */
  readonly areaReset$ = this.areaResetSubject.asObservable();

  save<T>(key: string, state: T): void {
    if (this.isResettingKey(key)) return;

    try {
      const entry: StoredRefreshState<T> = {
        savedAt: new Date().toISOString(),
        state,
      };
      sessionStorage.setItem(this.storageKey(key), JSON.stringify(entry));
    } catch {
      // Storage can be unavailable or full. Refresh persistence is best-effort and
      // must never prevent the user from continuing their work.
    }
  }

  load<T>(key: string): T | null {
    try {
      const raw = sessionStorage.getItem(this.storageKey(key));
      if (!raw) return null;
      const entry = JSON.parse(raw) as StoredRefreshState<T>;
      return entry && typeof entry === 'object' && 'state' in entry ? entry.state : null;
    } catch {
      this.remove(key);
      return null;
    }
  }

  remove(key: string): void {
    try {
      sessionStorage.removeItem(this.storageKey(key));
    } catch {
      // See save(): storage failures should not break the application flow.
    }
  }

  saveNavigationState(url: string, state: unknown): void {
    const customState = customNavigationState(state);
    if (!customState) return;
    this.save(`navigation:${normalizeRouteUrl(url)}`, customState);
  }

  clearCurrentUser(): void {
    clearRefreshStateForCurrentUser();
  }

  /**
   * Clears the active menu's reload snapshot and tells its mounted page to return to
   * its landing state. Saving remains suppressed until the sidebar navigation has
   * completed, so routed child components cannot write stale state back on destroy.
   */
  resetArea(area: RefreshStateArea): void {
    this.resettingAreas.add(area);
    this.clearAreaState(area);
    this.areaResetSubject.next(area);

    const previousTimer = this.resetFallbackTimers.get(area);
    if (previousTimer) clearTimeout(previousTimer);
    this.resetFallbackTimers.set(area, setTimeout(() => this.finishAreaReset(area), 1000));
  }

  /** Completes an intentional reset after Angular has finished routing to the menu root. */
  finishAreaReset(area: RefreshStateArea): void {
    const timer = this.resetFallbackTimers.get(area);
    if (timer) clearTimeout(timer);
    this.resetFallbackTimers.delete(area);
    this.clearAreaState(area);
    this.resettingAreas.delete(area);
  }

  private clearAreaState(area: RefreshStateArea): void {
    try {
      const prefix = currentUserStoragePrefix();
      const matchingKeys: string[] = [];

      for (let index = 0; index < sessionStorage.length; index += 1) {
        const fullKey = sessionStorage.key(index);
        if (!fullKey?.startsWith(prefix)) continue;
        const logicalKey = fullKey.slice(prefix.length);
        if (this.keyBelongsToArea(logicalKey, area)) matchingKeys.push(fullKey);
      }

      matchingKeys.forEach(key => sessionStorage.removeItem(key));
    } catch {
      // An intentional reset must still allow navigation if storage is unavailable.
    }
  }

  private isResettingKey(key: string): boolean {
    return Array.from(this.resettingAreas).some(area => this.keyBelongsToArea(key, area));
  }

  private keyBelongsToArea(key: string, area: RefreshStateArea): boolean {
    const sharedWorkflowState =
      key.startsWith('workflow-runtime:') ||
      key.startsWith('log-sheet:') ||
      key.startsWith('navigation:/workflow-runtime') ||
      key.startsWith('navigation:/form-details');

    switch (area) {
      case 'home':
        return key === 'home' ||
          key === 'overview' ||
          key.startsWith('unit-log:') ||
          key.startsWith('navigation:/home') ||
          sharedWorkflowState;
      case 'dashboard':
        return key === 'dashboard' || key.startsWith('navigation:/dashboard') || sharedWorkflowState;
      case 'analytics':
        return key === 'analytics' || key.startsWith('navigation:/analytics');
      case 'reports':
        return key === 'reports' || key.startsWith('navigation:/reports');
      case 'settings':
        return key === 'settings-shell' ||
          key.startsWith('settings:') ||
          key.startsWith('navigation:/settings');
    }
  }

  private storageKey(key: string): string {
    return storageKey(key);
  }
}
